import { X } from 'lucide-react'
import { cn } from '../atoms'
import type { GmailSearchFilters } from '../../utils/searchQuery'

interface FilterChipsProps {
  filters: GmailSearchFilters
  criticidad?: string
  label?: string
  onRemove: (key: keyof GmailSearchFilters) => void
  onRemoveCriticidad?: () => void
  onRemoveLabel?: () => void
  onClearAll: () => void
}

const CRITICIDAD_LABELS: Record<string, { label: string; tone: string }> = {
  CRITICO_MAXIMO: { label: 'Criticidad: Crítico Máximo', tone: 'rose' },
  CRITICO: { label: 'Criticidad: Crítico', tone: 'red' },
  ALTO: { label: 'Criticidad: Alto', tone: 'amber' },
  MEDIO: { label: 'Criticidad: Medio', tone: 'green' },
  BAJO: { label: 'Criticidad: Bajo', tone: 'purple' },
  INFORMATIVO: { label: 'Criticidad: Informativo', tone: 'blue' },
}

/**
 * Chips de filtros activos estilo Gmail.
 * Cada chip muestra el operador + valor con un X para quitarlo individualmente.
 */
export function FilterChips({
  filters,
  criticidad,
  label,
  onRemove,
  onRemoveCriticidad,
  onRemoveLabel,
  onClearAll,
}: FilterChipsProps) {
  const chips: Array<{
    key: string
    label: string
    tone: string
    value: string
    onRemoveClick: () => void
  }> = []

  // Chips from URL query parameters
  if (criticidad) {
    const config = CRITICIDAD_LABELS[criticidad] || {
      label: `Criticidad: ${criticidad}`,
      tone: 'red',
    }
    chips.push({
      key: 'param_criticidad',
      label: config.label,
      value: criticidad,
      tone: config.tone,
      onRemoveClick: () => onRemoveCriticidad?.(),
    })
  }

  if (label) {
    const isUnread = label === 'UNREAD'
    chips.push({
      key: 'param_label',
      label: isUnread ? 'No leídos' : `Etiqueta: ${label}`,
      value: label,
      tone: isUnread ? 'rose' : 'amber',
      onRemoveClick: () => onRemoveLabel?.(),
    })
  }

  // Chips from Gmail search filters
  if (filters.from) {
    chips.push({ key: 'from', label: `De: ${filters.from}`, value: filters.from, tone: 'blue', onRemoveClick: () => onRemove('from') })
  }
  if (filters.to) {
    chips.push({ key: 'to', label: `Para: ${filters.to}`, value: filters.to, tone: 'blue', onRemoveClick: () => onRemove('to') })
  }
  if (filters.subject) {
    chips.push({ key: 'subject', label: `Asunto: "${filters.subject}"`, value: filters.subject, tone: 'blue', onRemoveClick: () => onRemove('subject') })
  }
  if (filters.status) {
    chips.push({ key: 'status', label: `Estado: ${filters.status}`, value: filters.status, tone: 'purple', onRemoveClick: () => onRemove('status') })
  }
  if (filters.label) {
    chips.push({ key: 'label', label: `Label: ${filters.label}`, value: filters.label, tone: 'amber', onRemoveClick: () => onRemove('label') })
  }
  if (filters.isUnread && !label) {
    chips.push({ key: 'isUnread', label: 'No leídos', value: 'unread', tone: 'rose', onRemoveClick: () => onRemove('isUnread') })
  }
  if (filters.isRead) {
    chips.push({ key: 'isRead', label: 'Leídos', value: 'read', tone: 'emerald', onRemoveClick: () => onRemove('isRead') })
  }
  if (filters.isStarred) {
    chips.push({ key: 'isStarred', label: 'Destacados', value: 'starred', tone: 'amber', onRemoveClick: () => onRemove('isStarred') })
  }
  if (filters.isUnstarred) {
    chips.push({ key: 'isUnstarred', label: 'Sin destacar', value: 'unstarred', tone: 'gray', onRemoveClick: () => onRemove('isUnstarred') })
  }
  if (filters.hasAttachment) {
    chips.push({ key: 'hasAttachment', label: 'Con adjuntos', value: 'attachment', tone: 'gray', onRemoveClick: () => onRemove('hasAttachment') })
  }
  if (filters.hasAlert) {
    chips.push({ key: 'hasAlert', label: 'Con alertas', value: 'alert', tone: 'red', onRemoveClick: () => onRemove('hasAlert') })
  }
  if (filters.hasCase) {
    chips.push({ key: 'hasCase', label: 'Con caso', value: 'case', tone: 'indigo', onRemoveClick: () => onRemove('hasCase') })
  }
  if (filters.afterDate) {
    chips.push({ key: 'afterDate', label: `Después: ${filters.afterDate}`, value: filters.afterDate, tone: 'green', onRemoveClick: () => onRemove('afterDate') })
  }
  if (filters.beforeDate) {
    chips.push({ key: 'beforeDate', label: `Antes: ${filters.beforeDate}`, value: filters.beforeDate, tone: 'green', onRemoveClick: () => onRemove('beforeDate') })
  }
  if (filters.freeText) {
    chips.push({ key: 'freeText', label: `Busca: "${filters.freeText}"`, value: filters.freeText, tone: 'slate', onRemoveClick: () => onRemove('freeText') })
  }

  if (chips.length === 0) return null

  const toneClasses: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/50',
    purple: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800/50',
    amber: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800/50',
    rose: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800/50',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/50',
    gray: 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
    red: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800/50',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800/50',
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800/50',
    slate: 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-900/30 dark:text-slate-300 dark:border-slate-800/50',
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {chips.map(chip => (
        <span
          key={chip.key}
          className={cn(
            'inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 text-xs font-medium border rounded-full',
            toneClasses[chip.tone],
          )}
        >
          {chip.label}
          <button
            type="button"
            onClick={chip.onRemoveClick}
            className="p-0.5 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
            aria-label={`Quitar filtro: ${chip.label}`}
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={onClearAll}
        className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 underline underline-offset-2 ml-1"
      >
        Limpiar todo
      </button>
    </div>
  )
}
